/**
 * Billing Custom Hooks
 *
 * Custom hooks wrapping TanStack Query for billing data.
 * Follows the established pattern of encapsulating useQuery in custom hooks.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { ShowToast } from "../../components/toast";
import { BillingHistoryResponse, PaymentMethod } from "../../types/billing";
import { api } from "../api";
import { getUser } from "../utils";

export interface BillingHistoryFilters {
  type?: "all" | "subscription" | "commission" | "fixed" | "hybrid";
  startDate?: string;
  endDate?: string;
  sortOrder?: "asc" | "desc";
  page?: number;
  itemsPerPage?: number;
}

/**
 * Get billing history for a user (subscription + commission charges)
 * @param userId - The user ID
 * @param filters - Optional filters for type, date range, sorting, and pagination
 */
export function useBillingHistory(
  userId: string | undefined,
  filters?: BillingHistoryFilters
) {
  return useQuery<BillingHistoryResponse>({
    queryKey: [
      "billing",
      "history",
      userId,
      filters?.type,
      filters?.startDate,
      filters?.endDate,
      filters?.sortOrder,
      filters?.page,
    ],
    queryFn: () => {
      const itemsPerPage = filters?.itemsPerPage || 25;
      const page = filters?.page || 1;
      const offset = (page - 1) * itemsPerPage;

      const options: Parameters<typeof api.billing.getHistory>[1] = {
        limit: itemsPerPage,
        offset,
      };

      // Only include type if it's not "all"
      if (filters?.type && filters.type !== "all") {
        options.type = filters.type;
      }

      if (filters?.startDate) {
        options.startDate = filters.startDate;
      }

      if (filters?.endDate) {
        options.endDate = filters.endDate;
      }

      // Note: sortOrder is handled client-side, not sent to API

      return api.billing.getHistory(userId!, options);
    },
    enabled: !!userId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Get payment method for a user
 * @param userId - The user ID
 */
export function usePaymentMethod(userId: string | undefined) {
  return useQuery<PaymentMethod>({
    queryKey: ["billing", "payment-method", userId],
    queryFn: () => api.billing.getPaymentMethod(userId!),
    enabled: !!userId,
  });
}

/**
 * Get billing status for a user (includes failed payment info)
 * @param userId - The user ID
 */
export function useBillingStatus(userId: string | undefined) {
  return useQuery({
    queryKey: ["billingStatus", userId],
    queryFn: () => api.billing.getBillingStatus(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    select: (res) => res.data,
  });
}

/**
 * Where Stripe should drop the customer once they're finished in the portal.
 * MarketingOnly users have no access to /settings, so they go back to their
 * own Billing surface (Marketing > Settings > Billing).
 */
function billingReturnUrl(): string {
  const user = getUser();
  const path =
    user?.type === "MarketingOnly"
      ? "/marketing?tab=settings&subtab=billing"
      : "/settings?tab=billing";
  return `${window.location.origin}${path}`;
}

/**
 * Send the user to the Stripe customer portal to manage their payment method.
 *
 * The portal URL is a single-use session minted per click, so this is a
 * mutation rather than a query — never prefetch or cache it. On success we
 * hand the browser off to Stripe, so the component unmounts mid-flight and
 * `isLoading` stays true until navigation happens (that's what keeps the
 * button in its pending state).
 */
export function useStripePortal() {
  return useMutation({
    mutationFn: (userId: string) =>
      api.billing.getStripePortalUrl(userId, billingReturnUrl()),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (error) => {
      console.error("Failed to get Stripe portal URL:", error);
      ShowToast({
        type: "error",
        message:
          "We couldn't open the billing portal. Please try again or contact support if this keeps happening.",
      });
    },
  });
}
